<script setup>
import NavbarComponent from "./components/NavbarComponent.vue";
import FooterComponent from '@/components/FooterComponent.vue';
import LoginModule from "./components/LoginModule.vue";
import ForgotPasswordOTP from "./components/ForgotPasswordOTP.vue";
import SignUp from "./components/SignUp.vue";
import PostalCode from "./components/PostalCode.vue";
import AddItem from "./components/AddItem.vue";
import MobileSidebar from "./components/MobileSidebar.vue";
import AddAddress from "./components/AddAddress.vue";
import OrderStatus from "./components/OrderStatus.vue";
import LoadingScreen from "./components/LoadingScreen.vue";
import ChatWidget from "./components/ChatWidget.vue";
import ToastHost from "./components/ToastHost.vue";
import { useTranslationStore } from "./stores/translation";
import { useAuthStore } from "./stores/auth";
import { useAddressStore } from "./stores/address";
import {  onBeforeMount, onMounted, computed, watch } from "vue";
import { useRoute } from "vue-router";
import { useWalletStore } from "./stores/wallet";
import { storeToRefs } from "pinia";

const translationStore = useTranslationStore();
const authStore = useAuthStore();
const addressStore = useAddressStore();
const walletStore = useWalletStore();

const { user } = storeToRefs(authStore);

const loadTranslations = async () => {
  try {
    await translationStore.fetchTranslations();
  } catch (error) {
    console.error('Failed to load translations:', error);
  }
  // the signed-in customer's details must load even when translations didn't
  if (user.value) {
    try {
      await authStore.fetchUserDetails();
      await addressStore.getEligiblePostalCodes();
      await addressStore.fetchAddresses();
      await walletStore.fetchWallet();
    } catch (error) {
      console.error('Failed to load account details:', error);
    }
  }
};

onBeforeMount(async () => {
  await loadTranslations();
});

// Site-wide LocalBusiness structured data (injected once; page-level JSON-LD is
// handled per view by useSEO).
onMounted(() => {
  try {
    if (typeof document === "undefined" || document.getElementById("ld-localbusiness")) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "FoodEstablishment",
      name: "CHDS 吃荤搭素",
      url: "https://chds.com.au",
      telephone: "+61489198036",
      email: "info@chds.com.au",
      address: {
        "@type": "PostalAddress",
        streetAddress: "243 Burwood Road",
        addressLocality: "Hawthorn East",
        addressRegion: "VIC",
        postalCode: "3122",
        addressCountry: "AU",
      },
      areaServed: "Melbourne",
      servesCuisine: "Chinese",
      priceRange: "$$",
      image: "https://chds.com.au/og-default.jpg",
    };
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = "ld-localbusiness";
    s.textContent = JSON.stringify(ld);
    document.head.appendChild(s);
  } catch (e) { /* structured data must never break the app */ }
});

watch(
     user,
     async(newUser, oldUser) => {
         if (newUser != null && newUser !== oldUser) {
             console.log("userupdated")
             await addressStore.getEligiblePostalCodes();
             await addressStore.fetchAddresses();
             await walletStore.fetchWallet();
         }
     }
 );

const route = useRoute();
// The staff dashboard and CHDS Circle are full-screen apps — hide the storefront
// chrome (nav, footer, customer modals) there so headers don't double up.
const isDashboard = computed(() => {
	const p = route.path || "";
	return p.startsWith("/dashboard") || p.startsWith("/circle");
});
// Partner co-branded ordering pages (/partner/<slug>) bring their own header: no storefront
// nav, footer or chat — but the customer modals (login, address) must still be mounted.
const isPartner = computed(() => (route.path || "").startsWith("/partner/"));
// The /box Meal Boxes page keeps the full storefront nav + footer so visitors who
// land there can browse the rest of the site and switch language.
// Find My Meals is a campaign landing page (Instagram / ads): it carries its own header,
// so the storefront nav, footer and chat bubble stay out of the way.
const isLanding = computed(() => { const p = route.path || ""; return p.startsWith("/find-my-meals") || p.startsWith("/m/"); });

const isLoaded = computed(() => translationStore.isLoaded);
const hasLoadError = computed(() => translationStore.loadError !== null);
const isRetrying = computed(() => translationStore.isLoading);

const handleRetry = async () => {
  await translationStore.reloadTranslations();
};
</script>

<template>
  <div v-if="isLoaded">
    <NavbarComponent v-if="!isDashboard && !isLanding && !isPartner" />
    <router-view />
    <template v-if="!isDashboard">
      <LoginModule />
      <ForgotPasswordOTP />
      <SignUp />
      <AddItem />
      <PostalCode />
      <MobileSidebar />
      <AddAddress />
      <OrderStatus />
      <FooterComponent v-if="!isLanding && !isPartner" />
    </template>
    <ChatWidget v-if="!isDashboard && !isPartner && !isLanding" />
  </div>
  <div v-else>
    <LoadingScreen
      :has-error="hasLoadError"
      :is-retrying="isRetrying"
      @retry="handleRetry"
    />
  </div>
  <ToastHost />
</template>

<style>
/* Dashboard modal dialogs use `.overlay > .modal`, which collides with Bootstrap's
   global `.modal { display:none; position:fixed }` and hid every dialog (Edit order,
   Refund, Order detail, Email, Settings). Restore correct rendering. */
.overlay > .modal {
  display: block;
  position: relative;
  inset: auto;
  height: auto;
}

/* Stop iOS Safari from auto-zooming when a field is focused (it zooms whenever the
   input font-size is under 16px, then the user has to pinch back out). */
@media (max-width: 768px) {
  .dash input, .dash select, .dash textarea,
  .overlay input, .overlay select, .overlay textarea {
    font-size: 16px !important;
  }
}

/* Full-screen detail overlays (orders/customers/wallets) on mobile: a sticky header
   with a clear Back button so staff can always get back to the list. */
.ovback { display: none; }
@media (max-width: 820px) {
  .detail .dh {
    position: sticky; top: 0; z-index: 2; background: #fff;
    margin: -16px -16px 12px; padding: 14px 16px;
    border-bottom: 1px solid #eef0ea; align-items: center;
  }
  .ovback {
    display: inline-flex; align-items: center; gap: 7px;
    border: none; background: #f1f2ed; color: #2f5018; font-weight: 600;
    border-radius: 9px; padding: 9px 14px; font-size: 14px; cursor: pointer; margin-right: 10px;
    -webkit-tap-highlight-color: transparent;
  }
  .detail .dh .x { font-size: 22px; padding: 4px 6px; }
}
</style>
